import React from 'react';
import { Button, BottomSheet, SearchInput, Card, Spinner } from '@parsina/framework';
import { Plus } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { bffQuery } from '../../lib/bffClient.js';
import { LIST_DRUGS, SEARCH_DEBOUNCE_MS, SEARCH_LIMIT } from '../../screens/Prescription/Prescription.shared.js';
import { MIN_SEARCH_LENGTH } from '../../lib/patients.js';

// The formulary picker, for a phone.
//
// The desktop `DrugCombobox` is an absolutely-positioned popover hanging under
// a field — a desktop pattern that, on a phone, is drawn over the page by the
// software keyboard and reaches the bottom of the viewport after two results.
// §6 makes `BottomSheet` the default overlay for a task, so the search IS the
// sheet: the field sits at the top of it, the results fill the space directly
// above the keyboard, and nothing the dentist is typing into can be covered.
//
// The CONTRACT is the desktop's, unchanged: the same `listDrugs` query, the
// same 300 ms debounce, the same 30-result limit, the same two-character floor.
// And the same rule that matters most — what leaves this component is the
// `Drug` object carrying `id`, the stable slug the interaction sweep is keyed
// on. The Persian display name never becomes a search key.

// Visually hidden but still announced — the standard clip pattern.
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
  border: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
};

const MobileDrugSearch = ({ onAdd }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      bffQuery(LIST_DRUGS, { query: term, limit: SEARCH_LIMIT })
        .then((data) => {
          if (!alive) return;
          setResults(data?.listDrugs || []);
          setFailed(false);
        })
        .catch((err) => {
          if (!alive) return;
          console.error('[drugs] search failed:', err?.message);
          setResults([]);
          setFailed(true);
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  const select = (drug) => {
    onAdd(drug);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const term = query.trim();
  const searchable = term.length >= MIN_SEARCH_LENGTH;

  // What the live region says. The list is conditionally rendered, so its own
  // text can never be announced reliably — a live region has to be in the DOM
  // before its content changes. This one always is while the sheet is open.
  let announcement = '';
  if (searchable) {
    if (searching) announcement = t('prescription.search.searching');
    else if (failed) announcement = t('errors.load.drugs');
    else if (results.length === 0) announcement = t('prescription.search.empty');
    else announcement = t('prescription.search.results', { count: results.length });
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="rx-search-open"
        className="w-full min-h-11 gap-1.5"
      >
        <Plus size={16} aria-hidden />
        {t('prescription.search.label')}
      </Button>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('prescription.search.label')}
        closeLabel={t('mobile.close')}
        data-testid="rx-search-sheet"
      >
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('prescription.search.placeholder')}
          aria-label={t('prescription.search.placeholder')}
          clearLabel={t('common.actions.clearFilters')}
          autoFocus
          // dir="auto": the formulary is searched in either script. ≥1rem or
          // iOS zooms the page on focus (§6).
          inputProps={{ dir: 'auto', style: { fontSize: '1rem' }, 'data-testid': 'rx-search-input' }}
          style={{ width: '100%' }}
        />

        <div aria-live="polite" role="status" style={SR_ONLY}>
          {announcement}
        </div>

        <div
          data-testid="rx-search-results"
          data-state={
            !searchable ? 'idle' : searching ? 'loading' : failed ? 'error' : results.length ? 'content' : 'empty'
          }
          style={{ marginTop: '0.75rem' }}
        >
          {!searchable && (
            <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '0.5rem 0' }}>
              {t('prescription.search.hint')}
            </div>
          )}

          {searchable && searching && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0' }}>
              {/* Spinner is legal INLINE — a page-level bare spinner is not (§7). */}
              <Spinner size={16} />
              <span style={{ fontSize: '0.75rem', color: C.textMuted }}>
                {t('prescription.search.searching')}
              </span>
            </div>
          )}

          {searchable && !searching && failed && (
            <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '0.5rem 0' }}>
              {t('errors.load.drugs')}
            </div>
          )}

          {searchable && !searching && !failed && results.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '0.5rem 0' }}>
              {t('prescription.search.empty')}
            </div>
          )}

          {searchable && !searching && results.length > 0 && (
            <div role="listbox" aria-label={t('prescription.search.label')}>
              {results.map((drug) => (
                <Card
                  key={drug.id}
                  onClick={() => select(drug)}
                  role="option"
                  aria-selected={false}
                  data-testid={`rx-search-result-${drug.id}`}
                  style={{ padding: '0.625rem 0.75rem', marginBottom: '0.375rem', borderRadius: '0.75rem' }}
                >
                  <div dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                    {drug.name}
                  </div>
                  {(drug.genericName || drug.category) && (
                    <div dir="auto" style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                      {[drug.genericName, drug.category].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>
    </>
  );
};

export default MobileDrugSearch;
